import React, { useEffect, useState } from "react"
import { Button, Form, Modal, Row, Col, Spinner } from "react-bootstrap"
import moment from "moment"
import { useToast } from "@/components/Common/Toast"
import { parsedUser } from "@/common/GetCurrentUser"
import API from "@/common/API"

interface SchedulePlaceRouteModalProps {
  show: boolean
  onHide: () => void
  place: {
    id: string
    name: string
    address?: string
    sector?: string
    assignedToObj?: {
      firstName?: string
      lastName?: string
    }
    assignedTo?: string
  }
}

interface AgentOption {
  id: string
  name: string
  role: "Vendedor" | "Chofer"
}

const DAYS = [
  { dayIndex: 1, name: "Lunes" },
  { dayIndex: 2, name: "Martes" },
  { dayIndex: 3, name: "Miércoles" },
  { dayIndex: 4, name: "Jueves" },
  { dayIndex: 5, name: "Viernes" },
  { dayIndex: 6, name: "Sábado" },
]

export default function SchedulePlaceRouteModal({
  show,
  onHide,
  place,
}: SchedulePlaceRouteModalProps): JSX.Element {
  const user: any = parsedUser()
  const { success } = useToast()

  const [routeType, setRouteType] = useState<"sales" | "dispatch">("sales")
  const [targetWeek, setTargetWeek] = useState<"current" | "next">("current")
  const [selectedDay, setSelectedDay] = useState<number>(1)
  const [conduceNumber, setConduceNumber] = useState<string>("")
  const [notes, setNotes] = useState<string>("")

  // Agents list & selection
  const [agents, setAgents] = useState<AgentOption[]>([])
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(false)
  const [selectedAgentId, setSelectedAgentId] = useState<string>(place.assignedTo || "")
  const [assignedPersonName, setAssignedPersonName] = useState<string>(
    place.assignedToObj
      ? `${place.assignedToObj.firstName || ""} ${place.assignedToObj.lastName || ""}`.trim()
      : ""
  )

  const currentWeekStart = moment().startOf("isoWeek")
  const weekMoment = targetWeek === "current" ? currentWeekStart : moment(currentWeekStart).add(1, "week")
  const dayMoment = moment(weekMoment).add(selectedDay - 1, "days")

  // Load real agents/sellers/drivers from API
  useEffect(() => {
    if (!user?.OrganizationId) return
    let isMounted = true

    const loadAgents = async () => {
      setIsLoadingAgents(true)
      try {
        const res = await API.getAction(
          "agents/Summary",
          `OrganizationId=${user.OrganizationId}&Page=0&Quantity=60`
        )
        if (isMounted && res.data?.status === "ok" && Array.isArray(res.data.response)) {
          const mapped: AgentOption[] = res.data.response.map((a: any) => ({
            id: a.id || a.agentId || a.userId,
            name: `${a.firstName || ""} ${a.lastName || ""}`.trim() || a.name || "Agente",
            role: (a.role || "").toLowerCase().includes("driver") ? "Chofer" : "Vendedor",
          }))
          setAgents(mapped)

          // If place has assigned person, match it
          if (place.assignedTo) {
            const found = mapped.find((m) => m.id === place.assignedTo)
            if (found) {
              setSelectedAgentId(found.id)
              setAssignedPersonName(found.name)
            }
          } else if (mapped.length > 0 && !selectedAgentId) {
            const firstSales = mapped.find((m) => m.role === "Vendedor") || mapped[0]
            setSelectedAgentId(firstSales.id)
            setAssignedPersonName(firstSales.name)
          }
        }
      } catch (err) {
        console.warn("Could not load agents in schedule modal", err)
      } finally {
        if (isMounted) setIsLoadingAgents(false)
      }
    }

    loadAgents()
    return () => {
      isMounted = false
    }
  }, [user?.OrganizationId, place.assignedTo])

  // Handle agent select change
  const handleAgentChange = (agentId: string) => {
    setSelectedAgentId(agentId)
    const found = agents.find((a) => a.id === agentId)
    if (found) {
      setAssignedPersonName(found.name)
    } else if (place.assignedToObj && agentId === place.assignedTo) {
      setAssignedPersonName(
        `${place.assignedToObj.firstName || ""} ${place.assignedToObj.lastName || ""}`.trim()
      )
    }
  }

  const handleSave = () => {
    const weekKey = weekMoment.format("YYYY-[W]WW")
    const storageKey = `shearly_weekly_routes_${user?.OrganizationId || "default"}_${routeType}_${weekKey}`

    let existingStops: any[] = []
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) existingStops = JSON.parse(raw)
    } catch (e) {
      console.error(e)
    }

    const finalAssignedName =
      assignedPersonName ||
      agents.find((a) => a.id === selectedAgentId)?.name ||
      (place.assignedToObj
        ? `${place.assignedToObj.firstName || ""} ${place.assignedToObj.lastName || ""}`.trim()
        : "Vendedor Asignado")

    const newStop = {
      id: `stp-sched-${Date.now()}`,
      category: routeType,
      placeId: place.id,
      placeName: place.name,
      address: place.address || "Dirección registrada",
      sector: place.sector || "",
      contactPerson: place.assignedToObj
        ? `${place.assignedToObj.firstName || ""} ${place.assignedToObj.lastName || ""}`.trim()
        : "Encargado",
      stageName: "En seguimiento",
      date: dayMoment.format("YYYY-MM-DD"),
      dayOfWeek: selectedDay,
      orderIndex: existingStops.filter((s: any) => s.dayOfWeek === selectedDay).length + 1,
      assignedTo: selectedAgentId || place.assignedTo || "agt-1",
      assignedName: finalAssignedName,
      assignedRole: routeType === "sales" ? "agent" : "driver",
      sourceType: "manual",
      status: "pending",
      conduceNumber: routeType === "dispatch" ? conduceNumber : undefined,
      notes: notes || undefined,
    }

    existingStops.push(newStop)
    localStorage.setItem(storageKey, JSON.stringify(existingStops))

    const dayName = DAYS.find((d) => d.dayIndex === selectedDay)?.name || "Día seleccionado"
    success(
      "Rutas Semanales",
      `"${place.name}" programado para el ${dayName} (${dayMoment.format("DD MMM")}) con ${finalAssignedName}.`
    )
    onHide()
  }

  const filteredAgents = agents.filter((a) =>
    routeType === "sales" ? a.role === "Vendedor" : a.role === "Chofer"
  )

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="h5 fw-bold d-flex align-items-center gap-2">
          <i className="fas fa-calendar-plus text-primary" />
          Programar en Ruta Semanal
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-3">
        <div className="bg-primary-subtle p-2.5 rounded-3 mb-3 border border-primary-subtle">
          <div className="fw-bold text-primary small text-uppercase">Lugar / Obra</div>
          <div className="fw-bold text-dark fs-6">{place.name}</div>
          <div className="text-muted small">
            <i className="fas fa-map-marker-alt text-danger me-1" />
            {place.sector || place.address || "Dirección registrada"}
          </div>
        </div>

        <Form>
          {/* Tipo de Ruta */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-uppercase text-secondary">
              Tipo de Ruta
            </Form.Label>
            <div className="d-flex gap-2">
              <Button
                variant={routeType === "sales" ? "primary" : "outline-secondary"}
                className="flex-fill d-flex align-items-center justify-content-center gap-1.5 py-1.5 fw-semibold rounded-3"
                size="sm"
                onClick={() => setRouteType("sales")}
              >
                <i className="fas fa-user-tie" />
                Visita Comercial (Vendedor)
              </Button>
              <Button
                variant={routeType === "dispatch" ? "primary" : "outline-secondary"}
                className="flex-fill d-flex align-items-center justify-content-center gap-1.5 py-1.5 fw-semibold rounded-3"
                size="sm"
                onClick={() => setRouteType("dispatch")}
              >
                <i className="fas fa-truck" />
                Despacho (Logística TMS)
              </Button>
            </div>
          </Form.Group>

          <Row className="g-2 mb-3">
            {/* Semana */}
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">Semana</Form.Label>
                <Form.Select
                  size="sm"
                  className="rounded-3"
                  value={targetWeek}
                  onChange={(e) => setTargetWeek(e.target.value as "current" | "next")}
                >
                  <option value="current">Semana Actual (Sem. {currentWeekStart.isoWeek()})</option>
                  <option value="next">Próxima Semana (Sem. {currentWeekStart.isoWeek() + 1})</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Día */}
            <Col xs={6}>
              <Form.Group>
                <Form.Label className="small fw-semibold">Día de la Semana</Form.Label>
                <Form.Select
                  size="sm"
                  className="rounded-3"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(Number(e.target.value))}
                >
                  {DAYS.map((d) => (
                    <option key={d.dayIndex} value={d.dayIndex}>
                      {d.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Vendedor / Chofer Asignado (DROPDOWN SELECTOR) */}
          <Form.Group className="mb-3">
            <Form.Label className="small fw-semibold text-dark d-flex justify-content-between align-items-center">
              <span>{routeType === "sales" ? "Vendedor Responsable" : "Chofer / Vehículo Asignado"}</span>
              {isLoadingAgents && <Spinner animation="border" size="sm" variant="primary" />}
            </Form.Label>
            <Form.Select
              size="sm"
              className="rounded-3 fw-semibold text-dark border-secondary-subtle"
              value={selectedAgentId}
              onChange={(e) => handleAgentChange(e.target.value)}
            >
              {place.assignedToObj && (
                <option value={place.assignedTo || "place-assigned"}>
                  ⭐ {place.assignedToObj.firstName || ""} {place.assignedToObj.lastName || ""} (Asignado al lugar)
                </option>
              )}
              {filteredAgents.length > 0 ? (
                filteredAgents.map((agt) => (
                  <option key={agt.id} value={agt.id}>
                    {routeType === "sales" ? "👤" : "🚛"} {agt.name} ({agt.role})
                  </option>
                ))
              ) : (
                agents.map((agt) => (
                  <option key={agt.id} value={agt.id}>
                    👤 {agt.name} ({agt.role})
                  </option>
                ))
              )}
            </Form.Select>
          </Form.Group>

          {/* Si es despacho: No. de conduce */}
          {routeType === "dispatch" && (
            <Form.Group className="mb-3">
              <Form.Label className="small fw-semibold">No. de Conduce / Factura (opcional)</Form.Label>
              <Form.Control
                size="sm"
                className="rounded-3"
                placeholder="Ej. CD-89021 o F-10294"
                value={conduceNumber}
                onChange={(e) => setConduceNumber(e.target.value)}
              />
            </Form.Group>
          )}

          {/* Notas */}
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold">Objetivo / Notas de la Parada (opcional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              size="sm"
              className="rounded-3"
              placeholder={
                routeType === "sales"
                  ? "Ej. Dar seguimiento a cotización de varillas y verificar avance de obra"
                  : "Ej. Descarga en rampa norte, entregar a encargado Ing. Santos"
              }
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="outline-secondary" size="sm" onClick={onHide} className="rounded-3">
          Cancelar
        </Button>
        <Button variant="primary" size="sm" onClick={handleSave} className="fw-semibold rounded-3 shadow-xs">
          <i className="fas fa-check me-1" />
          Guardar en la Ruta
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
